import type React from 'react';

/**
 * Personenfarben — jede Person hat im ganzen ServiceHub dieselbe Farbe (v1.395.0). Vergeben und
 * gespeichert wird auf dem Server (`backend/src/lib/userColors.ts`, Spalte `users.color`); die
 * Palette steht dort und hier — laufen die auseinander, zeigt die Oberfläche eine gespeicherte
 * Farbe nicht an, also beide ändern.
 *
 * Werte sind Mantine-Palettennamen: `color={…}` an Badge/Avatar oder `var(--mantine-color-<name>-6)`.
 *
 * `slate` (v1.406.0) ist Grau mit orangem Rand: reserviert (nur Admins wählen sie, die automatische
 * Vergabe nimmt sie nie). Wer eine Personenfarbe anzeigt, hängt `personRingStyle(color)` an — sonst
 * sieht die Person aus wie „abgesagt"/„unbekannt" (reines Grau).
 */
export const PERSON_COLORS = [
  'blue',
  'lime',
  'grape',
  'teal',
  'pink',
  'yellow',
  'violet',
  'green',
  'cyan',
  'indigo',
  'orange',
  'red',
  'slate',
] as const;

export type PersonColor = (typeof PERSON_COLORS)[number];

export const PERSON_COLOR_LABEL: Record<PersonColor, string> = {
  blue: 'Blau',
  lime: 'Hellgrün',
  grape: 'Lila',
  teal: 'Petrol',
  pink: 'Pink',
  yellow: 'Gelb',
  violet: 'Violett',
  green: 'Grün',
  cyan: 'Türkis',
  indigo: 'Indigo',
  orange: 'Orange',
  red: 'Rot',
  slate: 'Grau (orange umrandet)',
};

/** Farben mit orangem Rand — dieselbe Liste wie die reservierten im Backend. */
export const RINGED_PERSON_COLORS: readonly PersonColor[] = ['slate'];

/** Farben, die die Ersatz-Farbe aus der ID verteilt (ohne reservierte). */
const FALLBACK_PERSON_COLORS: readonly PersonColor[] = PERSON_COLORS.filter((c) => !RINGED_PERSON_COLORS.includes(c));

export const PERSON_RING_COLOR = 'var(--fb-orange)';

export function hasPersonRing(color: string | null | undefined): boolean {
  return !!color && (RINGED_PERSON_COLORS as readonly string[]).includes(color);
}

/**
 * Oranger Rand für Flächen in der Personenfarbe (Punkt, Badge, Avatar, Kalendereintrag). `inset` für
 * Flächen, deren Rand nicht über den Inhalt hinausragen darf (Zellen, Listen-Karten).
 */
export function personRingStyle(
  color: string | null | undefined,
  { width = 2, inset = false }: { width?: number; inset?: boolean } = {},
): React.CSSProperties | undefined {
  if (!hasPersonRing(color)) return undefined;
  return { boxShadow: `${inset ? 'inset ' : ''}0 0 0 ${width}px ${PERSON_RING_COLOR}` };
}

/** Für Symbole (Icons) in der Personenfarbe: oranger Schein statt Rand. */
export function personIconStyle(color: string | null | undefined): React.CSSProperties | undefined {
  if (!hasPersonRing(color)) return undefined;
  return { filter: `drop-shadow(0 0 1px ${PERSON_RING_COLOR}) drop-shadow(0 0 1px ${PERSON_RING_COLOR})` };
}

/** Farbe für Personen ohne ServiceHub-Konto (nur ein Name aus weclapp) und „niemand". */
export const UNKNOWN_PERSON_COLOR = 'gray';

export function isPersonColor(value: unknown): value is PersonColor {
  return typeof value === 'string' && (PERSON_COLORS as readonly string[]).includes(value);
}

/**
 * Gespeicherte Farbe, sonst eine feste aus der ID — für den Moment zwischen Anlage und erstem
 * Abruf (der Server vergibt beim nächsten Laden) und für alte Antworten ohne `color`.
 */
export function personColor(color: string | null | undefined, id: string | null | undefined): string {
  if (isPersonColor(color)) return color;
  if (!id) return UNKNOWN_PERSON_COLOR;
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return FALLBACK_PERSON_COLORS[hash % FALLBACK_PERSON_COLORS.length];
}

/** Hintergrund-Ton für Flächen (Kalender, Leisten) — `-6` wie der Rest der Oberfläche. */
export function personColorVar(color: string, shade = 6): string {
  return `var(--mantine-color-${color}-${shade})`;
}

export interface ColoredPerson {
  id: string;
  displayName: string;
  color?: string | null;
  weclappUserId?: string | null;
  cwMemberIdentifier?: string | null;
}

export interface PersonColorIndex {
  /**
   * Farbe zu einer Kennung — ServiceHub-ID, weclapp-Benutzer (`responsibleUserId`, auch
   * `local:<id>`), ConnectWise-Kürzel oder Anzeigename; die erste bekannte gewinnt. Wer nicht im
   * Team ist (weclapp-Benutzer ohne ServiceHub-Konto, Ausgeschiedene), bekommt Grau.
   */
  colorOf: (...refs: Array<string | null | undefined>) => string;
  /** true, wenn eine der Kennungen zu jemandem im Team gehört */
  knows: (...refs: Array<string | null | undefined>) => boolean;
}

const nameKey = (name: string) => name.trim().toLowerCase();

export function buildPersonColorIndex(people: ColoredPerson[]): PersonColorIndex {
  const byKey = new Map<string, string>();
  const byName = new Map<string, string>();
  for (const p of people) {
    const color = personColor(p.color, p.id);
    byKey.set(p.id, color);
    byKey.set(`local:${p.id}`, color);
    if (p.weclappUserId) byKey.set(p.weclappUserId, color);
    if (p.cwMemberIdentifier) byKey.set(p.cwMemberIdentifier, color);
    // Namen nur, solange sie eindeutig sind — zwei „Peter" bekämen sonst beide die Farbe des zweiten.
    // '' = mehrdeutig, zählt als unbekannt.
    const key = nameKey(p.displayName);
    byName.set(key, byName.has(key) ? '' : color);
  }
  const find = (refs: Array<string | null | undefined>) => {
    for (const ref of refs) {
      if (!ref) continue;
      const hit = byKey.get(ref) ?? byName.get(nameKey(ref));
      if (hit) return hit;
    }
    return null;
  };
  return {
    colorOf: (...refs) => find(refs) ?? UNKNOWN_PERSON_COLOR,
    knows: (...refs) => find(refs) !== null,
  };
}
